/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler/async setState */
import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { DESC_FIELDS } from "./adminConstants";

export function ClubsTab({ api, isCentral, user, managedClubs }) {
  const [clubList, setClubList] = useState([]);
  const [editClub, setEditClub] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/clubs");
      const filtered = isCentral ? r.data : r.data.filter(c => managedClubs.some(mc => mc.id === c.id));
      setClubList(filtered.filter(c => !["ninersbillard", "sportuebertragungen", "shop"].includes(c.id)));
    } catch (e) { toast.error("Vereine konnten nicht geladen werden"); }
  }, [api, isCentral, managedClubs]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (club) => {
    const desc = club.description || {};
    setForm({
      membership_text: club.membership_text || "",
      statutes_text: club.statutes_text || "",
      subtitle: desc.subtitle || "",
      intro: desc.intro || "",
      text: desc.text || "",
      visionTitle: desc.visionTitle || "",
      vision: Array.isArray(desc.vision) ? desc.vision.join("\n") : "",
      quote: desc.quote || "",
      closing: desc.closing || "",
      email: desc.email || "",
      instagram: desc.instagram || "",
    });
    setEditClub(club);
  };

  const save = async () => {
    if (!editClub) return;
    const visionArr = form.vision ? form.vision.split("\n").map(s => s.trim()).filter(Boolean) : [];
    const desc = {};
    for (const f of DESC_FIELDS) {
      if (f.key === "vision") { if (visionArr.length) desc.vision = visionArr; }
      else if (form[f.key]) desc[f.key] = form[f.key];
    }
    try {
      await api.put(`/clubs/${editClub.id}/info`, {
        membership_text: form.membership_text,
        statutes_text: form.statutes_text,
        description: Object.keys(desc).length > 0 ? desc : null
      });
      toast.success("Vereinsinfo gespeichert");
      setEditClub(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="clubs-tab">
      <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>Vereinsverwaltung</h2>

      {editClub ? (
        <div className="bg-card border border-white/10 p-5" data-testid="club-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: editClub.accent }}>{editClub.name}</h3>
            <button onClick={() => setEditClub(null)} className="text-xs text-muted-foreground hover:text-foreground">Abbrechen</button>
          </div>
          <div className="space-y-4">
            <div className="border-b border-white/10 pb-4">
              <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Über Uns (Beschreibung)</Label>
              <div className="grid grid-cols-1 gap-3">
                {DESC_FIELDS.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <textarea value={form[f.key] || ""} onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                        rows={f.key === "vision" ? 4 : 3} placeholder={f.placeholder || ""}
                        className="w-full mt-1 p-2 text-sm bg-input/50 border border-white/10 text-foreground resize-y"
                        data-testid={`club-desc-${f.key}`} />
                    ) : (
                      <Input value={form[f.key] || ""} onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                        className="mt-1 bg-input/50 border-white/10 rounded-none" placeholder={f.placeholder || ""}
                        data-testid={`club-desc-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Mitgliedschaft</Label>
              <textarea value={form.membership_text} onChange={e => setForm(p => ({...p, membership_text: e.target.value}))}
                rows={3} className="w-full p-2 text-sm bg-input/50 border border-white/10 text-foreground resize-y"
                placeholder="Wie werde ich Mitglied?" data-testid="club-membership-text" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Statuten</Label>
              <textarea value={form.statutes_text} onChange={e => setForm(p => ({...p, statutes_text: e.target.value}))}
                rows={3} className="w-full p-2 text-sm bg-input/50 border border-white/10 text-foreground resize-y"
                placeholder="Vereinsstatuten" data-testid="club-statutes-text" />
            </div>
            <button onClick={save} className="px-6 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase" data-testid="club-save-btn">
              Speichern
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {clubList.map(c => (
            <div key={c.id} className="bg-card border border-white/5 p-4 flex items-center justify-between" data-testid={`club-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-8 rounded-full" style={{ backgroundColor: c.accent }} />
                <div>
                  <p className="font-bold text-sm">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.type}{c.description ? ' | Beschreibung vorhanden' : ''}</p>
                </div>
              </div>
              <button onClick={() => startEdit(c)} className="px-3 py-1.5 text-xs font-bold uppercase border border-white/10 hover:bg-white/5 flex items-center gap-1"
                data-testid={`club-edit-${c.id}`}>
                <Pencil className="w-3 h-3" /> Bearbeiten
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
